import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  Alert,
  IconButton,
  LinearProgress,
} from '@mui/material';
import {
  Close,
  Launch,
  Warning,
  Error,
  CheckCircle,
  Info,
  Business,
  AttachMoney,
  Schedule,
  Gavel,
  Assessment,
  LocationOn,
  Security,
  Flag,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import { hideDetailModal } from '../store/slices/fraudAnalysisSlice';
import { fraudUtils } from '../services/api';
import DeepResearchModal from './DeepResearchModal';

const DetailModal: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { showModal, contractDetails } = useSelector((state: RootState) => state.fraudAnalysis);
  const [deepResearchOpen, setDeepResearchOpen] = useState(false);

  const handleClose = () => {
    dispatch(hideDetailModal());
  };

  const handleDeepResearch = () => {
    setDeepResearchOpen(true);
  };

  const handleUSASpendingLink = () => {
    if (contractDetails) {
      // Use actual URL from API response, fallback to generated if none
      const url = contractDetails.url || 
                 contractDetails.usaspending_permalink || 
                 contractDetails.sam_link || 
                 fraudUtils.generateUSASpendingLink(contractDetails);
      if (url) window.open(url, '_blank');
    }
  };

  const getRiskIcon = (riskLevel: string) => {
    switch (riskLevel?.toUpperCase()) {
      case 'CRITICAL':
        return <Error sx={{ color: '#d32f2f' }} />;  // Red
      case 'HIGH':
        return <Warning sx={{ color: '#f57c00' }} />;  // Orange
      case 'MEDIUM':
        return <Warning sx={{ color: '#fbc02d' }} />;  // Yellow
      case 'LOW':
        return <Info sx={{ color: '#2196f3' }} />;  // Blue
      case 'LEGITIMATE':
        return <CheckCircle sx={{ color: '#4caf50' }} />;  // Green
      default:
        return <Info sx={{ color: '#9e9e9e' }} />;
    }
  };


  if (!contractDetails) {
    return null;
  }

  const analysis = contractDetails.fraud_analysis;
  const riskLevel = contractDetails.fraud_risk_level || 'UNKNOWN';

  return (
    <Dialog
      open={showModal}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: 'white',
        borderBottom: 1,
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {getRiskIcon(riskLevel)}
          <Box>
            <Typography variant="h5">
              Contract Details
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {contractDetails.title || contractDetails.description || 'Government Contract Analysis'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={`${riskLevel} Risk`}
            color={
              riskLevel === 'CRITICAL' || riskLevel === 'HIGH' ? 'error' :
              riskLevel === 'MEDIUM' ? 'warning' : 'default'
            }
            variant="outlined"
            icon={getRiskIcon(riskLevel)}
          />
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Contract Overview */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Gavel /> Contract Overview
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <Business fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                    <strong>Vendor:</strong> {
                      contractDetails.vendor || 
                      contractDetails.vendor_name || 
                      contractDetails.recipient_name || 
                      contractDetails.awardee_or_recipient_legal_business_name ||
                      'N/A'
                    }
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <AttachMoney fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                    <strong>Amount:</strong> {fraudUtils.formatCurrency(contractDetails.total_obligated_amount || contractDetails.amount || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Agency:</strong> {contractDetails.agency || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <Schedule fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                    <strong>Award Date:</strong> {fraudUtils.formatDate(contractDetails.date || contractDetails.award_date)}
                  </Typography>
                  {contractDetails.award_id && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Award ID:</strong> {contractDetails.award_id}
                    </Typography>
                  )}
                  {contractDetails.source_type && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Source Type:</strong> {contractDetails.source_type}
                    </Typography>
                  )}
                  {contractDetails.solicitation_info && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Solicitation Type:</strong> {contractDetails.solicitation_info.code} - {contractDetails.solicitation_info.description}
                    </Typography>
                  )}
                  {contractDetails.award_details?.contracting_agency && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Contracting Agency:</strong> {contractDetails.award_details.contracting_agency}
                    </Typography>
                  )}
                  {contractDetails.award_details?.performance_location && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <LocationOn fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                      <strong>Performance Location:</strong> {contractDetails.award_details.performance_location}
                    </Typography>
                  )}
                </Box>
                
                {/* Emergency-Specific Information */}
                {contractDetails.disaster_emergency_fund_codes && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" gutterBottom sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                      🚨 Emergency Declaration
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Emergency Fund Code:</strong>
                      </Typography>
                      <Chip 
                        label={contractDetails.disaster_emergency_fund_codes}
                        color={
                          contractDetails.disaster_emergency_fund_codes.includes('COVID') ? 'error' :
                          contractDetails.disaster_emergency_fund_codes.includes('Disaster') ? 'warning' :
                          contractDetails.disaster_emergency_fund_codes.includes('Wildfire') ? 'warning' :
                          'info'
                        }
                        size="small"
                        sx={{ mb: 1 }}
                      />
                      
                      {(contractDetails.extent_competed || contractDetails.number_of_offers_received !== undefined || contractDetails.solicitation_procedures) && (
                        <>
                          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 1, fontWeight: 'bold' }}>
                            Competition Analysis:
                          </Typography>
                          
                          {contractDetails.extent_competed && (
                            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ ml: 2 }}>
                              <strong>Competition Status:</strong> {contractDetails.extent_competed}
                              {contractDetails.extent_competed.includes('NOT COMPETED') && (
                                <Chip label="No Competition" color="warning" size="small" sx={{ ml: 1 }} />
                              )}
                            </Typography>
                          )}
                          
                          {contractDetails.number_of_offers_received !== undefined && (
                            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ ml: 2 }}>
                              <strong>Offers Received:</strong> {contractDetails.number_of_offers_received}
                              {contractDetails.number_of_offers_received === 0 && (
                                <Chip label="No Bids" color="error" size="small" sx={{ ml: 1 }} />
                              )}
                              {contractDetails.number_of_offers_received === 1 && (
                                <Chip label="Single Bid" color="warning" size="small" sx={{ ml: 1 }} />
                              )}
                              {contractDetails.number_of_offers_received >= 2 && contractDetails.number_of_offers_received <= 3 && (
                                <Chip label="Limited Competition" color="info" size="small" sx={{ ml: 1 }} />
                              )}
                              {contractDetails.number_of_offers_received > 3 && (
                                <Chip label="Good Competition" color="success" size="small" sx={{ ml: 1 }} />
                              )}
                            </Typography>
                          )}
                          
                          {(contractDetails.solicitation_procedures || contractDetails.solicitation_procedures_code) && (
                            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ ml: 2 }}>
                              <strong>Solicitation Method:</strong> {
                                contractDetails.solicitation_procedures || ''
                              }
                              {contractDetails.solicitation_procedures_code && (
                                <Chip 
                                  label={`Code: ${contractDetails.solicitation_procedures_code}`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ ml: 1 }}
                                />
                              )}
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                  </>
                )}
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                  {contractDetails.description || 'No description available'}
                </Typography>
                
                {/* Additional Contract Details */}
                <Box sx={{ mt: 2 }}>
                  {/* Performance Period - Prominently displayed */}
                  {(contractDetails.period_of_performance_start_date || contractDetails.period_of_performance_current_end_date) && (
                    <Box sx={{ mb: 1.5, p: 1, bgcolor: 'rgba(0,0,0,0.03)', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <Schedule fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                        <strong>Performance Period:</strong>
                      </Typography>
                      <Typography variant="body2" sx={{ ml: 3 }}>
                        Start: {fraudUtils.formatDate(contractDetails.period_of_performance_start_date) || 'N/A'}
                      </Typography>
                      <Typography variant="body2" sx={{ ml: 3 }}>
                        End: {fraudUtils.formatDate(contractDetails.period_of_performance_current_end_date) || 'N/A'}
                      </Typography>
                    </Box>
                  )}
                  
                  {contractDetails.naics_code && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>NAICS Code:</strong> {contractDetails.naics_code}
                      {contractDetails.naics_description && ` - ${contractDetails.naics_description}`}
                    </Typography>
                  )}
                  
                  {contractDetails.product_or_service_code && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Product/Service Code:</strong> {contractDetails.product_or_service_code}
                      {contractDetails.product_or_service_description && ` - ${contractDetails.product_or_service_description}`}
                    </Typography>
                  )}
                  
                  {/* Vendor UEI for tracking */}
                  {(contractDetails.vendor_uei || contractDetails.awardee_or_recipient_uei_number) && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Vendor UEI:</strong> {contractDetails.vendor_uei || contractDetails.awardee_or_recipient_uei_number}
                    </Typography>
                  )}
                  
                  {(contractDetails.location || contractDetails.place_of_performance_city) && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Performance Location:</strong> {
                        contractDetails.location ? 
                          `${contractDetails.location.city || contractDetails.place_of_performance_city || ''}, ${contractDetails.location.state || contractDetails.place_of_performance_state || ''}` :
                          `${contractDetails.place_of_performance_city || ''}, ${contractDetails.place_of_performance_state || ''}`
                      }
                    </Typography>
                  )}
                  
                  {contractDetails.contracting_officers_determination_of_business_size && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Business Size:</strong> {
                        contractDetails.contracting_officers_determination_of_business_size === 'S' ? 'Small Business' :
                        contractDetails.contracting_officers_determination_of_business_size === 'O' ? 'Other than Small' :
                        contractDetails.contracting_officers_determination_of_business_size
                      }
                    </Typography>
                  )}
                  
                  {contractDetails.type_of_set_aside && contractDetails.type_of_set_aside !== 'NONE' && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Set-Aside Type:</strong> {contractDetails.type_of_set_aside}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Competition Integrity Section */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Gavel /> Competition Integrity
                </Typography>
                
                {/* Number of Offers with visual indicator */}
                {contractDetails.number_of_offers_received !== undefined && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Number of Offers Received:</strong> {contractDetails.number_of_offers_received}
                      {contractDetails.number_of_offers_received === 0 && (
                        <Chip label="No Bids" color="error" size="small" sx={{ ml: 1 }} />
                      )}
                      {contractDetails.number_of_offers_received === 1 && (
                        <Chip label="Single Bid" color="warning" size="small" sx={{ ml: 1 }} />
                      )}
                      {contractDetails.number_of_offers_received === 2 && (
                        <Chip label="Limited Competition" color="warning" size="small" sx={{ ml: 1 }} />
                      )}
                      {contractDetails.number_of_offers_received > 2 && contractDetails.number_of_offers_received <= 5 && (
                        <Chip label="Moderate Competition" color="info" size="small" sx={{ ml: 1 }} />
                      )}
                      {contractDetails.number_of_offers_received > 5 && (
                        <Chip label="Good Competition" color="success" size="small" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                  </Box>
                )}
                
                {/* Competition Status */}
                {contractDetails.extent_competed && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Competition Status:</strong> {contractDetails.extent_competed}
                    </Typography>
                  </Box>
                )}
                
                {/* Solicitation Method */}
                {(contractDetails.solicitation_procedures || contractDetails.solicitation_procedures_code) && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Solicitation:</strong> {
                        contractDetails.solicitation_procedures || contractDetails.solicitation_procedures_code
                      }
                    </Typography>
                  </Box>
                )}
                
                {/* Contract Type */}
                {contractDetails.type_of_contract_pricing && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Contract Type:</strong> {contractDetails.type_of_contract_pricing}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Fraud Risk Analysis */}
          <Grid item xs={12} md={6}>
            <Card sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Security /> Fraud Risk Assessment
                </Typography>
                
                {analysis && (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Risk Assessment Scores
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, analysis.net_score || 0)}
                        color={
                          (analysis.net_score || 0) > 60 ? 'error' :
                          (analysis.net_score || 0) > 30 ? 'warning' : 'success'
                        }
                        sx={{ height: 8, borderRadius: 4, mb: 1 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Net Risk Score: {analysis.net_score || 0}/100
                      </Typography>
                    </Box>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Legitimacy Score:</strong>
                        </Typography>
                        <Typography variant="h6" color="success.main">
                          {analysis.legitimacy_score || 0}%
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Suspicion Score:</strong>
                        </Typography>
                        <Typography variant="h6" color="error.main">
                          {analysis.suspicion_score || 0}%
                        </Typography>
                      </Grid>
                    </Grid>

                    <Typography variant="body2" color="text.secondary">
                      <strong>Investigation Required:</strong> {analysis.requires_investigation ? 'Yes' : 'No'}
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Risk Factors - Simplified */}
          {(contractDetails.fraud_risk_factors?.length || 0) > 0 && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Flag /> Risk Factors
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {contractDetails.fraud_risk_factors?.map((factor: string, index: number) => (
                      <Chip
                        key={index}
                        icon={<Warning fontSize="small" />}
                        label={factor}
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Emergency/Timing Analysis if available */}
          {(contractDetails.emergency_analysis || contractDetails.timing_analysis) && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Warning /> Special Analysis
                  </Typography>
                  
                  {/* Emergency Analysis */}
                  {contractDetails.emergency_analysis && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Emergency Procurement Analysis:</strong>
                      </Typography>
                      {contractDetails.emergency_analysis.emergency_type && (
                        <Chip 
                          label={contractDetails.emergency_analysis.emergency_type}
                          color="warning"
                          size="small"
                          sx={{ mb: 1 }}
                        />
                      )}
                      {contractDetails.emergency_analysis.misuse_indicators && contractDetails.emergency_analysis.misuse_indicators.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="error">
                            Misuse Indicators:
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                            {contractDetails.emergency_analysis.misuse_indicators.map((indicator: string, idx: number) => (
                              <Chip
                                key={idx}
                                label={indicator.replace(/_/g, ' ').toLowerCase()}
                                size="small"
                                color="error"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                      {contractDetails.emergency_analysis.risk_score && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                          Risk Score: {contractDetails.emergency_analysis.risk_score}/100
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  {/* Timing Analysis */}
                  {contractDetails.timing_analysis && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Timing Analysis:</strong>
                      </Typography>
                      {contractDetails.timing_analysis.anomalies && contractDetails.timing_analysis.anomalies.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="warning.main">
                            Timing Anomalies:
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                            {contractDetails.timing_analysis.anomalies.map((anomaly: string, idx: number) => (
                              <Chip
                                key={idx}
                                label={anomaly.replace(/_/g, ' ').toLowerCase()}
                                size="small"
                                color="warning"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                      {contractDetails.timing_analysis.risk_score && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                          Risk Score: {contractDetails.timing_analysis.risk_score}/100
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Analysis Details */}
          {analysis && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Assessment /> Detailed Analysis
                  </Typography>
                  
                  {analysis.reasoning && analysis.reasoning.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Analysis Reasoning:</strong>
                      </Typography>
                      <Box sx={{ ml: 2 }}>
                        {analysis.reasoning.map((reason: string, index: number) => (
                          <Typography key={index} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            • {reason}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {analysis.legitimate_indicators && analysis.legitimate_indicators.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Legitimate Indicators:</strong>
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {analysis.legitimate_indicators.map((indicator: string, index: number) => (
                          <Chip
                            key={index}
                            label={indicator}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {analysis.suspicious_indicators && analysis.suspicious_indicators.length > 0 && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Suspicious Indicators:</strong>
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {analysis.suspicious_indicators.map((indicator: string, index: number) => (
                          <Chip
                            key={index}
                            label={indicator}
                            size="small"
                            color="error"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Irregularity Indicators - Only show if different from risk factors */}
          {contractDetails.irregularity_indicators && 
           Object.entries(contractDetails.irregularity_indicators).filter(([_, value]) => value).length > 0 && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Irregularity Indicators
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {Object.entries(contractDetails.irregularity_indicators)
                      .filter(([_, value]) => value)
                      .map(([key]) => (
                        <Chip
                          key={key}
                          label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          color="warning"
                          size="small"
                        />
                      ))
                    }
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Final Recommendation */}
          {analysis?.recommendation && (
            <Grid item xs={12}>
              <Alert 
                severity={
                  riskLevel === 'HIGH' ? 'error' :
                  riskLevel === 'MEDIUM' ? 'warning' : 'info'
                }
                sx={{ fontSize: '1rem' }}
              >
                <Typography variant="body1">
                  <strong>Investigation Recommendation:</strong> {analysis.recommendation}
                </Typography>
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: 'grey.50' }}>
        <Button onClick={handleClose}>
          Close
        </Button>
        <Button
          variant="outlined"
          onClick={handleUSASpendingLink}
          startIcon={<Launch />}
        >
          View on USAspending.gov
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Assessment />}
          onClick={handleDeepResearch}
        >
          Do Deep Research
        </Button>
      </DialogActions>
      
      {/* Deep Research Modal */}
      {contractDetails && (
        <DeepResearchModal
          open={deepResearchOpen}
          onClose={() => setDeepResearchOpen(false)}
          contract={contractDetails}
        />
      )}
    </Dialog>
  );
};

export default DetailModal;